import { Trans, useLingui } from '@lingui/react/macro'
import { DatePicker, Input, Popconfirm, Select } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useGeckoNotifications } from 'features/notifications'
import { formatOverlayTimestamp } from 'features/timeSeries/components/chartOverlays/overlayChrome'
import { buildLabelViewWindow } from 'features/timeSeries/components/labelTool/labelGeometry'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import {
  collectLabelSensorIds,
  EMPTY_LABEL_FILTERS,
  filterLabels,
  hasActiveLabelFilters,
  type LabelGroupFilterValue,
} from 'features/timeSeries/labels/labelFilters'
import {
  getLabelGroup,
  LABEL_GROUPS,
} from 'features/timeSeries/labels/labelGroups'
import type { ProcessDataLabel } from 'features/timeSeries/labels/processDataLabelsService'
import { useProcessDataLabels } from 'features/timeSeries/labels/useProcessDataLabels'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { TimeSeriesService } from 'features/timeSeries/timeSeriesService'
import { planPenAdditions } from 'features/timeSeries/utils/chartPenAdditions'
import { Check, Crosshair, Loader2, Plus, Search, Trash2 } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const FilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.5rem 0.375rem;
  flex-shrink: 0;
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  /* The pickers must be free to shrink inside a narrow sidebar. */
  > * {
    flex: 1;
    min-width: 0;
  }
`

const GroupChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.1875rem;
`

const GroupChip = styled.button<{ $color: string; $selected: boolean }>`
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: 1px solid ${({ $color }) => $color};
  border-radius: 0.1875rem;
  background: ${({ $color, $selected }) =>
    $selected ? $color : 'transparent'};
  cursor: pointer;

  &:hover {
    box-shadow: 0 0 0 1px ${({ $color }) => $color};
  }
`

const BulkAction = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${CHART_COLORS.CURSOR_ACCENT};
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

const FilterSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const SummaryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ClearFiltersButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 0.6875rem;
  color: ${CHART_COLORS.CURSOR_ACCENT};
  cursor: pointer;
`

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
`

const Row = styled.div<{ $isOnChart: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  border: 1px solid
    ${({ theme, $isOnChart }) =>
      $isOnChart
        ? theme.forecast.activeText
        : theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  background: ${({ theme, $isOnChart }) =>
    $isOnChart ? theme.forecast.background3 : 'none'};

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
  }
`

const ToggleArea = styled.button`
  flex: 1;
  min-width: 0;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
`

const RowActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  margin: 0.375rem 0.375rem 0.375rem 0;
  flex-shrink: 0;
  align-self: stretch;
`

const RowActionsTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const AddTagsButton = styled.button`
  padding: 0.125rem 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.1875rem;
  border: none;
  border-radius: 0.25rem;
  background: none;
  box-shadow: inset 0 0 0 1px
    ${({ theme }) => theme.forecast.cardDividerBackground};
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.forecast.activeText};
    color: ${({ theme }) => theme.forecast.activeText};
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

const GoToLabelButton = styled.button`
  flex-shrink: 0;
  padding: 0.125rem 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.1875rem;
  border: none;
  border-radius: 0.25rem;
  background: none;
  box-shadow: inset 0 0 0 1px ${CHART_COLORS.CURSOR_ACCENT};
  color: ${CHART_COLORS.CURSOR_ACCENT};
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: ${CHART_COLORS.CURSOR_ACCENT_LIGHT};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const DeleteButton = styled.button`
  flex-shrink: 0;
  padding: 0.1875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.25rem;
  background: none;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.forecast.cardDividerBackground};
    color: ${({ theme }) => theme.forecast.dangerText};
  }
`

const GroupBadge = styled.span<{ $color: string }>`
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  /* Pinned to the card's bottom-right, below the actions. */
  margin-top: auto;
  border: 1px solid ${({ $color }) => $color};
  border-radius: 0.1875rem;
  background: ${({ $color }) => $color};
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  max-width: 100%;
`

const Name = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const OnChartMark = styled(Check)`
  width: 0.75rem;
  height: 0.75rem;
  flex-shrink: 0;
  color: ${({ theme }) => theme.forecast.activeText};
`

const Detail = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PenList = styled.ul`
  list-style: none;
  margin: 0.125rem 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.1875rem;
  max-width: 100%;
`

const Pen = styled.li`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.1875rem;
  padding: 0 0.25rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Description = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  max-width: 100%;
  overflow-wrap: anywhere;
`

const StatusNote = styled.div`
  padding: 1rem 0.5rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const Spinner = styled(Loader2)`
  width: 0.875rem;
  height: 0.875rem;
  animation: ${spin} 1s linear infinite;
`

export const LabelsTab: React.FC = () => {
  const { t } = useLingui()

  const {
    siteID,
    sensorEntries,
    selectedSensors,
    visibleLabelIds,
    setVisibleLabelIds,
    setSelectedSensors,
    setTimeRange,
    setDateMode,
    setLiveTail,
  } = useScatterplotContext()

  // A second subscription to the same SWR key as the chart's label tool; the
  // shared cache dedupes the fetch and keeps both views on one list.
  const { hoveredLabelId, setHoveredLabelId } = useCursorData()
  const { labels, isLoading, deleteLabel } = useProcessDataLabels(siteID)
  const { errorNotification, warningNotification } = useGeckoNotifications()

  const [filters, setFilters] = useState(EMPTY_LABEL_FILTERS)

  // Closing the sidebar mid-hover would otherwise leave a label promoted on the
  // chart with nothing pointing at it.
  useEffect(() => () => setHoveredLabelId(null), [setHoveredLabelId])

  const sortedLabels = useMemo(
    () => [...labels].sort((a, b) => b.startMs - a.startMs),
    [labels]
  )

  // A label's pens are whatever it was scoped to, which may include sensors
  // that are not on this chart — fall back to the id so the row still names it.
  const penNamesById = useMemo(() => {
    const names = new Map<string, string>()
    ;[...sensorEntries, ...selectedSensors].forEach((sensor) => {
      const name = sensor.data?.name
      if (name) names.set(sensor.id, name)
    })
    return names
  }, [selectedSensors, sensorEntries])

  const penNameFor = useCallback(
    (sensorId: string) => penNamesById.get(sensorId) ?? sensorId,
    [penNamesById]
  )

  const visibleLabels = useMemo(
    () => filterLabels(sortedLabels, filters, penNameFor),
    [filters, penNameFor, sortedLabels]
  )

  // A filtered-out row unmounts without firing mouseleave, which would strand
  // its flag promoted on the chart.
  useEffect(() => {
    if (
      hoveredLabelId !== null &&
      !visibleLabels.some((label) => label.id === hoveredLabelId)
    ) {
      setHoveredLabelId(null)
    }
  }, [hoveredLabelId, setHoveredLabelId, visibleLabels])

  const tagOptions = useMemo(
    () =>
      collectLabelSensorIds(sortedLabels)
        .map((sensorId) => ({ value: sensorId, label: penNameFor(sensorId) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [penNameFor, sortedLabels]
  )

  const toggleGroupFilter = useCallback(
    (groupId: LabelGroupFilterValue) =>
      setFilters((current) => ({
        ...current,
        groupIds: current.groupIds.includes(groupId)
          ? current.groupIds.filter((id) => id !== groupId)
          : [...current.groupIds, groupId],
      })),
    []
  )

  const handleRangeChange = useCallback(
    (range: [Dayjs | null, Dayjs | null] | null) =>
      setFilters((current) => ({
        ...current,
        fromMs: range?.[0]?.startOf('day').valueOf() ?? null,
        toMs: range?.[1]?.endOf('day').valueOf() ?? null,
      })),
    []
  )

  // The site pool runs to thousands of entries, so it is indexed once here
  // rather than per row: a row only needs to know whether anything is missing.
  const sensorEntriesById = useMemo(
    () => new Map(sensorEntries.map((sensor) => [sensor.id, sensor])),
    [sensorEntries]
  )

  const plottedSensorIds = useMemo(
    () => new Set(selectedSensors.map((sensor) => sensor.id)),
    [selectedSensors]
  )

  const addReferencedTags = useCallback(
    (label: ProcessDataLabel) => {
      const { additions, unresolved } = planPenAdditions(
        label.sensorIds,
        selectedSensors,
        sensorEntriesById
      )

      if (additions.length > 0) {
        setSelectedSensors((current) => [...current, ...additions])
      }

      if (unresolved.length > 0) {
        warningNotification(
          t`${{ value454_15: unresolved.length }} of this label's pens are not in this site's tag list and were not added.`
        )
      }
    },
    [
      selectedSensors,
      sensorEntriesById,
      setSelectedSensors,
      warningNotification,
      t,
    ]
  )

  // The bulk action follows the list: with a filter applied it adds or removes
  // exactly what is on screen, not the whole site.
  const allListedOnChart =
    visibleLabels.length > 0 &&
    visibleLabels.every((label) => visibleLabelIds.includes(label.id))

  const toggleAllListed = useCallback(() => {
    const listedIds = visibleLabels.map((label) => label.id)
    setVisibleLabelIds((current) => {
      if (listedIds.every((id) => current.includes(id))) {
        return current.filter((id) => !listedIds.includes(id))
      }
      const missing = listedIds.filter((id) => !current.includes(id))
      return [...current, ...missing]
    })
  }, [setVisibleLabelIds, visibleLabels])

  const toggleOnChart = useCallback(
    (labelId: string) =>
      setVisibleLabelIds((current) =>
        current.includes(labelId)
          ? current.filter((id) => id !== labelId)
          : [...current, labelId]
      ),
    [setVisibleLabelIds]
  )

  /**
   * Pin the chart to the label's window. An absolute range and live tail are
   * mutually exclusive — live tail would re-anchor this window to "now" — and
   * the interval is re-derived for the new span so the bucket budget holds.
   */
  const goToLabel = useCallback(
    (label: ProcessDataLabel) => {
      const { fromMs, toMs } = buildLabelViewWindow(label)
      const { interval, interval_unit } =
        TimeSeriesService.getIntervalForPenCount(
          Math.max(1, Math.round((toMs - fromMs) / 1000)),
          selectedSensors.length
        )

      setDateMode('absolute')
      setLiveTail(false)
      setTimeRange({
        from: new Date(fromMs).toISOString(),
        to: new Date(toMs).toISOString(),
        preset: 'custom',
        interval,
        intervalUnit: interval_unit,
      })
      setVisibleLabelIds((current) =>
        current.includes(label.id) ? current : [...current, label.id]
      )
    },
    [
      selectedSensors.length,
      setDateMode,
      setLiveTail,
      setTimeRange,
      setVisibleLabelIds,
    ]
  )

  const removeLabel = useCallback(
    async (labelId: string) => {
      try {
        await deleteLabel(labelId)
        setVisibleLabelIds((current) => current.filter((id) => id !== labelId))
      } catch (error) {
        errorNotification(
          t`Failed to delete label: ${{ value536_39: error instanceof Error ? error.message : String(error) }}`
        )
      }
    },
    [deleteLabel, errorNotification, setVisibleLabelIds, t]
  )

  if (isLoading && labels.length === 0) {
    return (
      <Container>
        <StatusNote>
          <Spinner />
          <Trans> Loading labels…</Trans>
        </StatusNote>
      </Container>
    )
  }

  if (sortedLabels.length === 0) {
    return (
      <Container>
        <StatusNote>
          <Trans>
            No labels at this site yet. Draw one with the label tool on the
            chart.
          </Trans>
        </StatusNote>
      </Container>
    )
  }

  return (
    <Container>
      <FilterBar>
        <Input
          size='small'
          allowClear
          value={filters.query}
          placeholder={t`Search labels, pens…`}
          aria-label={t`Search labels`}
          prefix={<Search size={12} />}
          onChange={(event) =>
            setFilters((current) => ({ ...current, query: event.target.value }))
          }
        />
        <FilterRow>
          <DatePicker.RangePicker
            size='small'
            allowEmpty={[true, true]}
            value={[
              filters.fromMs ? dayjs(filters.fromMs) : null,
              filters.toMs ? dayjs(filters.toMs) : null,
            ]}
            placeholder={[t`From`, t`To`]}
            onChange={handleRangeChange}
          />
        </FilterRow>
        <FilterRow>
          <Select
            size='small'
            showSearch
            allowClear
            optionFilterProp='label'
            placeholder='Any tag'
            aria-label='Filter by tag'
            value={filters.sensorId}
            options={tagOptions}
            onChange={(sensorId) =>
              setFilters((current) => ({
                ...current,
                sensorId: sensorId ?? null,
              }))
            }
          />
        </FilterRow>
        <GroupChips>
          {LABEL_GROUPS.map((group) => (
            <GroupChip
              key={group.id}
              type='button'
              $color={group.accent}
              $selected={filters.groupIds.includes(group.id)}
              aria-label={t`Filter by ${{ value618_41: group.name }} group`}
              aria-pressed={filters.groupIds.includes(group.id)}
              title={group.name}
              onClick={() => toggleGroupFilter(group.id)}
            />
          ))}
          <GroupChip
            type='button'
            $color='#8a8a8a'
            $selected={filters.groupIds.includes('ungrouped')}
            aria-label={t`Filter by ungrouped`}
            aria-pressed={filters.groupIds.includes('ungrouped')}
            title={t`Ungrouped`}
            onClick={() => toggleGroupFilter('ungrouped')}
          />
        </GroupChips>
        <FilterSummary>
          <span>
            {hasActiveLabelFilters(filters)
              ? t`${{ value637_21: visibleLabels.length }} of ${{ value637_48: sortedLabels.length }} labels`
              : `${sortedLabels.length} labels`}
          </span>
          <SummaryActions>
            {hasActiveLabelFilters(filters) && (
              <ClearFiltersButton
                type='button'
                onClick={() => setFilters(EMPTY_LABEL_FILTERS)}>
                <Trans>Clear filters</Trans>
              </ClearFiltersButton>
            )}
            <BulkAction
              type='button'
              disabled={visibleLabels.length === 0}
              title={
                allListedOnChart
                  ? t`Take these labels off the chart`
                  : t`Put these labels on the chart`
              }
              onClick={toggleAllListed}>
              {allListedOnChart ? t`Remove all` : t`Add all`}
            </BulkAction>
          </SummaryActions>
        </FilterSummary>
      </FilterBar>

      {visibleLabels.length === 0 ? (
        <StatusNote>
          <Trans>No labels match these filters.</Trans>
        </StatusNote>
      ) : (
        <List>
          {visibleLabels.map((label) => {
            const isOnChart = visibleLabelIds.includes(label.id)
            const hasPensToAdd = label.sensorIds.some(
              (sensorId) => !plottedSensorIds.has(sensorId)
            )
            const group = getLabelGroup(label.groupId)
            return (
              <Row
                key={label.id}
                $isOnChart={isOnChart}
                onMouseEnter={() => setHoveredLabelId(label.id)}
                onMouseLeave={() => setHoveredLabelId(null)}>
                <ToggleArea
                  type='button'
                  aria-pressed={isOnChart}
                  title={isOnChart ? t`Remove from chart` : t`Add to chart`}
                  onClick={() => toggleOnChart(label.id)}>
                  <NameRow>
                    {isOnChart && <OnChartMark />}
                    <Name>{label.name || t`Untitled label`}</Name>
                  </NameRow>
                  <Detail>
                    {formatOverlayTimestamp(label.startMs)}
                    {' — '}
                    {formatOverlayTimestamp(label.endMs)}
                  </Detail>
                  {label.sensorIds.length > 0 && (
                    <PenList>
                      {label.sensorIds.map((sensorId) => (
                        <Pen key={sensorId}>
                          {penNamesById.get(sensorId) ?? sensorId}
                        </Pen>
                      ))}
                    </PenList>
                  )}
                  {label.description && (
                    <Description>{label.description}</Description>
                  )}
                </ToggleArea>
                <RowActions>
                  <RowActionsTop>
                    <GoToLabelButton
                      type='button'
                      aria-label={t`Go to ${{ value712_45: label.name || 'Untitled label' }}`}
                      onClick={() => goToLabel(label)}>
                      <Crosshair size={11} />
                      <Trans>Go to Label</Trans>
                    </GoToLabelButton>
                    <Popconfirm
                      title={t`Delete this label?`}
                      description={t`It is removed from every chart at this site.`}
                      okText={t`Delete`}
                      cancelText={t`Cancel`}
                      onConfirm={() => void removeLabel(label.id)}>
                      <DeleteButton
                        type='button'
                        aria-label={t`Delete ${{ value725_48: label.name || 'Untitled label' }}`}>
                        <Trash2 size={13} />
                      </DeleteButton>
                    </Popconfirm>
                  </RowActionsTop>
                  <AddTagsButton
                    type='button'
                    disabled={!hasPensToAdd}
                    title={
                      !hasPensToAdd
                        ? t`This label's tags are already on the chart`
                        : t`Add referenced tags to chart`
                    }
                    aria-label={t`Add referenced tags from ${{ value738_62: label.name || 'Untitled label' }} to chart`}
                    onClick={() => addReferencedTags(label)}>
                    <Plus size={11} />
                    <Trans>Add referenced tags</Trans>
                  </AddTagsButton>
                  {group && (
                    <GroupBadge
                      $color={group.accent}
                      title={`${group.name} group`}
                    />
                  )}
                </RowActions>
              </Row>
            )
          })}
        </List>
      )}
    </Container>
  )
}
